import { useParams } from '@reach/router';
import React from 'react';
import { useAsync } from 'react-async';

import {
  getActiveDataset,
  getActiveDbVersion,
  getActiveSearchId,
} from '../../../../../core/utils/utils';
import { useAppGoogleReCaptcha } from '../../../../../hooks/useAppGoogleReCaptcha';
import { useBenchmarkState } from '../../../../Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../shared/ErrorContext/ErrorContext.types';

export function useCompanyTabLoader<T>(
  getData: (id: string, token: string, dataset?: string, dbVersion?: string) => Promise<T>,
) {
  const { company, dataset, dbVersion } = useCompaniesState();
  const { activeBenchmark } = useBenchmarkState();
  const { companyUid, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const resolvedCompanyUid = companyUid || company?.companyInfo?.id;
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const dispatchErrors = useErrorsDispatch();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const promiseFn = React.useCallback(() => {
    if (!executeRecaptcha) {
      return Promise.reject(new Error('Recaptcha not available'));
    }
    return executeRecaptcha('search').then((token) =>
      getData(resolvedCompanyUid!, token, activeDataset || undefined, activeDbVersion || undefined),
    );
  }, [resolvedCompanyUid, executeRecaptcha, getData, activeDataset, activeDbVersion]);

  const { data, isLoading, error } = useAsync({
    promiseFn,
    onReject: (error) => {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      return Promise.reject(error);
    },
  });

  return { data, isLoading, error };
}

export function useCompanyFinTabLoader<T>(
  getData: (
    id: string,
    searchId: string | null,
    token: string,
    dataset?: string,
    dbVersion?: string,
  ) => Promise<T>,
) {
  const { company, searchId, dataset, dbVersion } = useCompaniesState();
  const { activeBenchmark } = useBenchmarkState();
  const activeSearchId = getActiveSearchId(searchId, activeBenchmark);
  const { companyUid, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const resolvedCompanyUid = companyUid || company?.companyInfo?.id;
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const dispatchErrors = useErrorsDispatch();

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const promiseFn = React.useCallback(() => {
    if (!executeRecaptcha) {
      return Promise.reject(new Error('Recaptcha not available'));
    }
    return executeRecaptcha('search').then((token) =>
      getData(
        resolvedCompanyUid!,
        activeSearchId || null,
        token,
        activeDataset || undefined,
        activeDbVersion || undefined,
      ),
    );
  }, [
    resolvedCompanyUid,
    activeSearchId,
    executeRecaptcha,
    getData,
    activeDataset,
    activeDbVersion,
  ]);

  const { data, isLoading, error } = useAsync({
    promiseFn,
    onReject: (error) => {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      return Promise.reject(error);
    },
  });

  return { data, isLoading, error };
}

export function useCompanyRawFinTabLoader<T>(
  getData: (id: string, token: string, dataset?: string, dbVersion?: string) => Promise<T>,
) {
  const { company, dataset, dbVersion } = useCompaniesState();
  const { activeBenchmark } = useBenchmarkState();
  const { companyUid, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const resolvedCompanyUid = companyUid || company?.companyInfo?.id;
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const dispatchErrors = useErrorsDispatch();

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const promiseFn = React.useCallback(() => {
    if (!executeRecaptcha) {
      return Promise.reject(new Error('Recaptcha not available'));
    }
    return executeRecaptcha('search').then((token) =>
      getData(resolvedCompanyUid!, token, activeDataset || undefined, activeDbVersion || undefined),
    );
  }, [resolvedCompanyUid, executeRecaptcha, getData, activeDataset, activeDbVersion]);

  const { data, isLoading, error } = useAsync({
    promiseFn,
    onReject: (error) => {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      return Promise.reject(error);
    },
  });

  return { data, isLoading, error };
}
